import React from 'react';
import { MapPin, Clock, CheckCircle, Trophy, User } from 'lucide-react';

export default function SalesRepList({ reps, loading, selectedRep, onSelectRep }) {
  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900 mb-3"></div>
        <p className="text-gray-500 text-sm">Loading sales reps...</p>
      </div>
    );
  }

  if (reps.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        <User size={40} className="mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No sales reps found</p>
        <p className="text-sm mt-1">Try changing the date or town filter</p>
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 py-3 border-b bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
          Sales Representatives ({reps.length})
        </h2>
      </div>

      <div className="divide-y">
        {reps.map((rep) => {
          const isSelected = selectedRep?.id === rep.id;
          return (
            <button
              key={rep.id}
              onClick={() => onSelectRep(rep)}
              className={`w-full text-left px-4 py-3.5 hover:bg-primary-50 transition ${
                isSelected ? 'bg-primary-50 border-l-4 border-primary-900' : 'border-l-4 border-transparent'
              }`}
            >
              {/* Name & Town */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  {/* Profile Photo or Fallback */}
                  {rep.photo_url ? (
                    <img
                      src={rep.photo_url}
                      alt={rep.name}
                      className={`w-8 h-8 rounded-full object-cover ${
                        rep.status === 'disabled' ? 'grayscale opacity-60' : ''
                      }`}
                    />
                  ) : (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                      isSelected ? 'bg-primary-900' : 'bg-primary-400'
                    }`}>
                      {rep.name?.charAt(0) || '?'}
                    </div>
                  )}
                  <div>
                    <p className={`font-semibold text-sm ${isSelected ? 'text-primary-900' : 'text-gray-800'}`}>
                      {rep.name}
                    </p>
                    {rep.town && (
                      <p className="text-xs text-gray-400 flex items-center gap-0.5">
                        <MapPin size={10} /> {rep.town}
                      </p>
                    )}
                  </div>
                </div>

                {/* Status & Bonus Badges */}
                <div className="flex items-center gap-1">
                  {rep.status === 'disabled' && (
                    <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full">
                      Disabled
                    </span>
                  )}
                  {rep.bonus_eligible && (
                    <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-0.5">
                      <Trophy size={10} /> Bonus
                    </span>
                  )}
                </div>
              </div>

              {/* Stats Row */}
              <div className="flex items-center gap-4 ml-10 text-xs text-gray-500">
                {/* Login Time */}
                <span className="flex items-center gap-1">
                  <Clock size={11} className="text-gray-400" />
                  {rep.login_at
                    ? new Date(rep.login_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '—'}
                </span>

                {/* Logout Time */}
                <span>
                  →{' '}
                  {rep.logout_at
                    ? new Date(rep.logout_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : 'Active'}
                </span>

                {/* Visits */}
                <span className="flex items-center gap-1">
                  <CheckCircle size={11} className="text-green-500" />
                  {rep.total_visits || 0} visits
                </span>
              </div>

              {/* Visit Progress Bar */}
              <div className="ml-10 mt-2">
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      (rep.total_visits || 0) >= 10 ? 'bg-green-500' : 'bg-primary-400'
                    }`}
                    style={{ width: `${Math.min(((rep.total_visits || 0) / 12) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {rep.total_visits || 0} / 12 daily target
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
